import { useCallback, useMemo, useReducer } from 'react';
import { useParams } from 'react-router';
import { type QueryClient } from '@tanstack/react-query';

import { type Clock } from '#packages/data-collection/services/clock';
import { type LoggingContext } from '#packages/data-collection/types/logging-context';
import {
  type Audit,
  type AuditId,
} from '#packages/data-collection/models/audit';
import {
  type QueryPageState,
  type MutationPageState,
} from '#packages/data-collection/types/page-state';
import { useMenu } from '#packages/web/hooks/use-menu';
import { useDialog } from '#packages/web/hooks/use-dialog';

import { useDeleteAuditByIdMutation } from '#apps/client-web/features/audit/hooks/use-delete-audit-by-id-mutation';
import { useLoadAuditByIdQuery } from '#apps/client-web/features/audit/hooks/use-load-audit-by-id-query';
import { type AuditService } from '#apps/client-web/features/audit/services/audit';
import { AUDITS_QUERY_KEY } from '#apps/client-web/features/audit/types/react-query';
import { useNavigateBack } from '#apps/client-web/app/hooks/use-navigate-back';
import { type AuditFormService } from '#apps/client-web/features/audit-form/services/audit-form';
import { type FileStorageService } from '#apps/client-web/features/file-storage/services/file-storage';
import { type SyncApiService } from '#apps/client-web/features/sync/services/sync-api';
import {
  useSyncAuditMutation,
  type AuditSyncState,
} from '#apps/client-web/features/sync/hooks/use-sync-audit-mutation';
import { useAuth } from '#apps/client-web/features/auth/hooks/use-auth';

export type AuditDetailsPageViewModel = ReturnType<
  typeof useAuditDetailsPageViewModel
>;

type AuditDetailsPageViewModelProps = LoggingContext & {
  auditService: AuditService;
  auditFormService: AuditFormService;
  fileStorageService: FileStorageService;
  syncApiService: SyncApiService;
  queryClient: QueryClient;
  clock: Clock;
};

export type UpdateSyncStateAction =
  | {
      type: 'update';
      payload: Partial<Exclude<AuditSyncState, 'forms' | 'attachments'>>;
    }
  | {
      type: 'update_forms';
      payload: Partial<AuditSyncState['forms']>;
    }
  | {
      type: 'update_attachments';
      payload: Partial<AuditSyncState['attachments']>;
    }
  | {
      type: 'reset';
    };

const initialSyncState = Object.freeze({
  preparing: 'idle',
  forms: {
    background: 'idle',
    mechanical: 'idle',
    checklist: 'idle',
  },
  attachments: {
    background: 'idle',
    mechanical: 'idle',
  },
} satisfies AuditSyncState);

const syncStateReducer = (
  state: AuditSyncState,
  action: UpdateSyncStateAction,
): AuditSyncState => {
  switch (action.type) {
    case 'update_forms': {
      return {
        ...state,
        forms: {
          ...state.forms,
          ...action.payload,
        },
      };
    }
    case 'update_attachments': {
      return {
        ...state,
        attachments: {
          ...state.attachments,
          ...action.payload,
        },
      };
    }
    case 'update': {
      return {
        ...state,
        ...action.payload,
      };
    }
    case 'reset': {
      return { ...initialSyncState };
    }
  }
};

export const useAuditDetailsPageViewModel = ({
  auditService,
  auditFormService,
  fileStorageService,
  syncApiService,
  queryClient,

  logger,
}: AuditDetailsPageViewModelProps) => {
  const { auditId } = useParams<{ auditId: AuditId }>();
  const goBack = useNavigateBack({ logger });
  const { user } = useAuth({ logger });

  const auditDetailsQuery = useLoadAuditByIdQuery({
    auditService,
    queryClient,
    collectionName: 'active',
    auditId: auditId!,
  });

  // page state synchronization
  const pageState = useMemo<QueryPageState<Audit>>(() => {
    if (auditDetailsQuery.isSuccess) {
      return { state: 'success', data: auditDetailsQuery.data };
    }

    if (auditDetailsQuery.isLoading || auditDetailsQuery.isPending) {
      return { state: 'loading' };
    }

    if (auditDetailsQuery.isError) {
      return { state: 'error', error: auditDetailsQuery.error };
    }

    throw new Error('Unhandled query state');
  }, [
    auditDetailsQuery.isSuccess,
    auditDetailsQuery.isLoading,
    auditDetailsQuery.isPending,
    auditDetailsQuery.isError,
    auditDetailsQuery.error,
  ]);

  const mainMenu = useMenu();

  const [syncState, updateSyncState] = useReducer(syncStateReducer, {
    ...initialSyncState,
  });

  const resetSyncState = useCallback(
    () => updateSyncState({ type: 'reset' }),
    [],
  );

  const syncAuditMutation = useSyncAuditMutation({
    auditId: auditId!,
    queryClient,
    auditFormService,
    fileStorageService,
    syncApiService,
    getAccessToken: () => user?.access_token,
    logger,
    updateState: updateSyncState,
  });

  const syncMutationState = useMemo<MutationPageState>(() => {
    if (syncAuditMutation.isIdle) {
      return { state: 'idle' };
    }

    if (syncAuditMutation.isSuccess) {
      return { state: 'success', data: null };
    }

    if (syncAuditMutation.isPending) {
      return {
        state: 'pending',
      };
    }

    if (syncAuditMutation.isError) {
      return { state: 'error', error: syncAuditMutation.error };
    }

    throw new Error('Unhandled sync mutation state');
  }, [
    syncAuditMutation.isIdle,
    syncAuditMutation.isPending,
    syncAuditMutation.isSuccess,
    syncAuditMutation.isError,
    syncAuditMutation.error,
  ]);

  const syncDialog = useDialog<Audit>();
  const onSynchronize = useCallback(() => {
    if (pageState.state !== 'success') {
      logger?.debug('Cannot synchronize audit, page is not in success state');
      return;
    }

    mainMenu.close();

    syncDialog.open(auditDetailsQuery.data);

    logger?.debug(
      {
        auditId,
        pageState,
      },
      'Audit synchronization requested',
    );
  }, [auditId, pageState]);

  const onSyncConfirm = useCallback(() => {
    if (pageState.state !== 'success') {
      logger?.debug('Cannot sync, page is not in success state');
      return;
    }

    logger?.debug({ auditId }, 'Starting audit sync');
    syncAuditMutation.mutate(pageState.data, {
      onSuccess: (result) => {
        logger?.debug(
          { auditId, serverAuditId: result.id },
          'Audit sync completed',
        );
      },
      onError: (err) => {
        logger?.error({ err, auditId }, 'Audit sync failed');
      },
    });
  }, [pageState, syncAuditMutation.mutate, auditId]);

  const deleteDialog = useDialog<Audit>();
  const deleteAuditByIdMutation = useDeleteAuditByIdMutation({
    auditService,
    queryClient,
  });
  const onDelete = useCallback(
    ({ confirmed }: { confirmed: boolean }) => {
      if (pageState.state !== 'success') {
        logger?.debug('Cannot delete audit, page is not in success state');
        return;
      }

      mainMenu.close();

      // show confirmation dialog
      if (!confirmed) {
        logger?.debug('Opening audit deletion confirmation dialog');
        deleteDialog.open(pageState.data);
        return;
      }

      // perform deletion
      deleteAuditByIdMutation.mutate(
        {
          auditId: auditId!,
          collectionName: 'active',
        },
        {
          onSuccess: (audit) => {
            logger?.debug({ auditId, audit }, 'Audit deleted successfully');

            // invalidate audit list queries
            queryClient
              .invalidateQueries({
                queryKey: [AUDITS_QUERY_KEY],
              })
              .then(() => {
                logger?.debug('Audit list queries invalidated');
                goBack();
              })
              .catch((err) => {
                logger?.error(
                  { err },
                  'Failed to invalidate audit list queries',
                );
              });
          },

          onError: (err) => {
            logger?.error({ err, auditId }, 'Failed to delete audit');

            throw err;
          },
        },
      );
    },
    [auditId, pageState],
  );

  return {
    mainMenu,

    auditDetailsQuery,

    auditId,
    page: pageState,

    onSynchronize,
    syncState,
    syncDialog,
    syncMutationState,
    isSyncSuccess: syncAuditMutation.isSuccess,
    onSyncConfirm,
    resetSyncState,

    onDelete,
    deleteDialog,
  };
};
